/* eslint-disable react-hooks/exhaustive-deps */
import {
  Box,
  Button,
  Checkbox,
  Grid,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Option,
  Icon,
  Select,
  Space,
  Table,
  TableHeader,
  TableHeaderRow,
  TableHeaderCell,
  TableBody,
  TableRow,
  TableCell,
  Text,
  TextLabel,
} from '@tonic-one/react';
import { DeleteIcon } from '@tonic-one/react-icons';
import { useTranslation } from 'react-i18next';
import {
  DlpKeywordListModalNameEnum,
  DlpKeywordListModalState,
  KeywordConditionEnum,
} from '../../type';
import {
  DlpKeywordDetail,
  DlpKeywordDictionaryKeyword,
} from '@Policy/utils/types';
import { memo, useEffect, useState } from 'react';
import i18next, { getFixedT } from 'i18next';
import { PolicyApi } from '@Apis';
import LoadingBtn from '@Components/Common/LoadingBtn';
import cloneDeep from 'lodash/cloneDeep';
import IconButton from '@Components/IconButton';
import { PermissionNameEnum } from '@Types/permissions';
import { usePermissionStore } from '../../../../../../models/usePermissionModel';

export const EditOrAddModal = ({
  modalState,
  onClose,
  refreshTable,
}: {
  onClose: () => void;
  modalState: DlpKeywordListModalState;
  refreshTable: () => void;
}) => {
  const { widthEditablePermission } = usePermissionStore();
  const dlpEditPermission = widthEditablePermission(
    PermissionNameEnum.dlpPolicy
  );
  const { t } = useTranslation('common');
  const keywordT = getFixedT(
    null,
    'policy',
    'policy.dataIdentifiers.keywordLists'
  );
  const [formData, setFormData] = useState<DlpKeywordDetail>({
    dpKeywordName: '',
    dpKeywordDesc: '',
    dpKeywordBuiltin: 0,
    dpKeywordCondition: 'All',
    dpKeywordThreshold: 1,
    dpKeywordNumber: 1,
    dictionary: {
      keyword: [],
    },
  });
  const disabled = formData.dpKeywordBuiltin === 1 || !dlpEditPermission;

  const setKeywordList = (v: DlpKeywordDictionaryKeyword[]) => {
    setFormData({
      ...formData,
      dictionary: {
        ...formData.dictionary,
        keyword: v,
      },
    });
  };

  //just for dpKeywordName
  const [error, setError] = useState(false);

  const onSave = async () => {
    if (formData.dpKeywordName === '') {
      setError(true);
      document.querySelector('#keywordListEdit')?.scrollTo(0, 0);
      return;
    }
    try {
      const cfgIndex = modalState?.data?.cfgIndex;
      if (cfgIndex) {
        await PolicyApi.updateDlpKeywordById(cfgIndex, formData);
      } else {
        await PolicyApi.addDlpKeyword(formData);
      }
      window.showNotification({
        titleMsg: keywordT('saveSuccessfully'),
        severity: 'success',
      });
      onClose();
      refreshTable();
    } catch (e) {
      window.showNotification({
        titleMsg: t('common.unknownError'),
        severity: 'error',
      });
    }
  };

  useEffect(() => {
    const cfgIndex = modalState?.data?.cfgIndex;
    const fetchData = async () => {
      if (cfgIndex) {
        const data = (
          await PolicyApi.getDlpKeywordById(cfgIndex, i18next.language)
        ).data;
        setFormData({
          ...formData,
          ...data,
          //data.dictionary.keyword maybe undefined
          dictionary: {
            keyword: [],
            ...data.dictionary,
          },
        });
      }
    };
    fetchData();
  }, []);

  return (
    <Modal
      ensureFocus={true}
      autoFocus={false}
      closeOnEsc={true}
      closeOnOutsideClick={false}
      isClosable={true}
      isOpen={
        modalState.name === DlpKeywordListModalNameEnum.Add ||
        modalState.name === DlpKeywordListModalNameEnum.Edit
      }
      onClose={onClose}
      size="lg"
    >
      <ModalOverlay opacity={0.9} />
      <ModalContent>
        <ModalHeader>{keywordT(`title.${modalState.name}`)}</ModalHeader>
        <ModalBody id="keywordListEdit">
          <BasicPropertiesForm
            formData={formData}
            setFormData={setFormData}
            error={error}
            setError={setError}
            disabled={disabled}
          />
          <KeywordContainer
            disabled={disabled}
            keywordList={formData.dictionary.keyword || []}
            setKeywordList={setKeywordList}
            dpKeywordCondition={
              formData.dpKeywordCondition as KeywordConditionEnum
            }
          />
        </ModalBody>
        <ModalFooter>
          <LoadingBtn disabled={disabled} onClick={onSave} variant="primary">
            {(loading: boolean) =>
              loading
                ? t('common.action.label.saving')
                : t('common.action.label.save')
            }
          </LoadingBtn>
          <Space width="2x" />
          <Button minWidth="20x" onClick={onClose}>
            {t('common.button.cancel')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

const KeywordContainer = ({
  keywordList,
  setKeywordList,
  disabled,
  dpKeywordCondition,
}: {
  keywordList: DlpKeywordDictionaryKeyword[];
  setKeywordList: (v: DlpKeywordDictionaryKeyword[]) => void;
  disabled: boolean;
  dpKeywordCondition: KeywordConditionEnum;
}) => {
  const { t } = useTranslation('common');
  const keywordT = getFixedT(
    null,
    'policy',
    'policy.dataIdentifiers.keywordLists'
  );

  const initItem = {
    value: '',
    caseSensitive: false,
    description: '',
    score: 1,
  };
  const [item, setItem] = useState<DlpKeywordDictionaryKeyword>(initItem);

  const [subError, setSubError] = useState<boolean>(false);
  //Behavior of old UI: empty characters are also counted as length
  let errorStr: string | null = null;
  if (subError) {
    if (item.value.length === 0) {
      errorStr = keywordT('error.subNameEmpty');
    } else if (item.value.length < 3) {
      errorStr = keywordT('error.subNameShort');
    }
  }

  const appendKeyword = () => {
    if (item.value.length < 3) {
      setSubError(true);
      return;
    }
    if (dpKeywordCondition === KeywordConditionEnum.Threshold) {
      setKeywordList([...keywordList, item]);
    } else {
      setKeywordList([
        ...keywordList,
        {
          ...item,
          score: 1,
        },
      ]);
    }
    setItem(initItem);
  };

  const deleteKeywordByIndex = (index: number) => {
    const copyBlockingList = cloneDeep(keywordList);
    copyBlockingList.splice(index, 1);
    setKeywordList(copyBlockingList);
  };

  return (
    <>
      <Text fontWeight="600" fontSize="18px" lineHeight="24px">
        {keywordT('KeywordDefine')}
      </Text>
      <Grid
        gridTemplateColumns="25% 75%"
        gridColumnGap="4px"
        gridRowGap="8px"
        pt="16px"
      >
        <TextLabel>{keywordT('label.keyword')}:</TextLabel>
        <Grid gridTemplateColumns="auto max-content" gridColumnGap="8px">
          <Input
            maxLength={512}
            value={item.value}
            onChange={(e: any) => {
              setSubError(false);
              setItem({ ...item, value: e.target.value });
            }}
          />
          <Checkbox
            onChange={(e: any) =>
              setItem({
                ...item,
                caseSensitive: e.target.checked,
              })
            }
            checked={item.caseSensitive}
            disabled={disabled}
          >
            {keywordT('label.caseSensitive')}
          </Checkbox>
        </Grid>
        {errorStr && (
          <>
            <Box></Box>
            <Text fontSize="sm" lineHeight="sm" color="red:50">
              {errorStr}
            </Text>
          </>
        )}
        {dpKeywordCondition === KeywordConditionEnum.Threshold && (
          <>
            <TextLabel>{keywordT('label.score')}:</TextLabel>
            <Input
              type="number"
              min="1"
              max="10"
              value={item.score + ''}
              onChange={(e: any) => {
                setItem({
                  ...item,
                  score: Math.max(0, Math.min(10, Number(e.target.value))),
                });
              }}
            ></Input>
          </>
        )}
        <Box></Box>
        <Box>
          <Button variant="primary" onClick={appendKeyword} disabled={disabled}>
            {t('common.action.label.add')}
          </Button>
        </Box>
      </Grid>
      <KeywordTable
        data={keywordList}
        removeByIndex={deleteKeywordByIndex}
        disabled={disabled}
      />
    </>
  );
};

//这是一个predefined的keyword list
// const a = {
//   cfgIndex: 'DP_KEYWORD_TM00000000-0000-0000-0002-000000000004',
//   dpKeywordBuiltin: 1,
//   dpKeywordCondition: 'Threshold',
//   dpKeywordDesc: 'Common source code functions/commands used in C/C++',
//   dpKeywordInternalname: '00000000-0000-0000-0002-000000000004',
//   dpKeywordModified: 0,
//   dpKeywordName: 'Source Code: C/C++',
//   dpKeywordThreshold: 17,
//   dictionary: {
//     keyword: [
//       {
//         value: 'gnu.org/licenses',
//         caseSensitive: true,
//         score: 3,
//         description: '',
//       },
//     ],
//     name: '00000000-0000-0000-0002-000000000004',
//     type: 'Threshold',
//     threshold: 17,
//     displayName: 'Source Code: C/C++',
//     description: 'Common source code functions/commands used in C/C++',
//     preDefined: true,
//   },
// };

const BasicPropertiesForm = memo(
  ({
    formData,
    setFormData,
    error,
    setError,
    disabled,
  }: {
    formData: DlpKeywordDetail;
    setFormData: (v: DlpKeywordDetail) => void;
    error: boolean;
    setError: (v: boolean) => void;
    disabled: boolean;
  }) => {
    const keywordT = getFixedT(
      null,
      'policy',
      'policy.dataIdentifiers.keywordLists'
    );

    const getInputAttr = (key: string) => {
      const data = formData[key as keyof typeof formData];
      const dataType = typeof data;
      return {
        disabled,
        value: data + '',
        onChange: (e: any) => {
          if (key === 'dpKeywordName') {
            setError(false);
          }
          let value: string | number = e.target.value;
          if (dataType === 'number') {
            value = Math.max(0, Math.min(1000, Number(e.target.value)));
          }
          setFormData({
            ...formData,
            [key]: value,
          });
        },
        ...(dataType === 'number' && {
          type: 'number',
          min: '0',
          max: '1000',
        }),
      };
    };

    return (
      <>
        <Text fontWeight="600" fontSize="18px" lineHeight="24px">
          {keywordT('basicProp')}
        </Text>
        <Grid
          gridTemplateColumns="25% 75%"
          gridColumnGap="4px"
          gridRowGap="8px"
          pt="16px"
        >
          <TextLabel className="required">
            {keywordT('label.dpKeywordName')}:
          </TextLabel>
          <Input
            maxLength={256}
            {...getInputAttr('dpKeywordName')}
            error={error}
          />
          <TextLabel>{keywordT('label.dpKeywordDesc')}:</TextLabel>
          <Input {...getInputAttr('dpKeywordDesc')} maxLength={512} />
          <TextLabel>{keywordT('label.dpKeywordCondition')}:</TextLabel>
          <Select
            disabled={disabled}
            variant="outline"
            value={formData.dpKeywordCondition}
            onChange={(e: any) => {
              setFormData({
                ...formData,
                dpKeywordCondition: e.target.value as KeywordConditionEnum,
              });
            }}
          >
            <Option value={KeywordConditionEnum.All}>
              {keywordT('condition.all')}
            </Option>
            <Option value={KeywordConditionEnum.Any}>
              {keywordT('condition.any')}
            </Option>
            <Option value={KeywordConditionEnum.Threshold}>
              {keywordT('condition.threshold')}
            </Option>
            <Option value={KeywordConditionEnum.Distance}>
              {keywordT('condition.distance')}
            </Option>
          </Select>
          {formData.dpKeywordCondition === KeywordConditionEnum.Threshold && (
            <>
              <TextLabel>{keywordT('label.dpKeywordThreshold')}:</TextLabel>
              <Input {...getInputAttr('dpKeywordThreshold')} />
            </>
          )}
          {formData.dpKeywordCondition === KeywordConditionEnum.Distance && (
            <>
              <TextLabel>{keywordT('label.dpKeywordNumber')}:</TextLabel>
              <Input {...getInputAttr('dpKeywordNumber')} />
            </>
          )}
        </Grid>
      </>
    );
  }
);

const KeywordTable = memo(
  ({
    data,
    removeByIndex,
    disabled,
  }: {
    data: DlpKeywordDictionaryKeyword[];
    removeByIndex: (index: number) => void;
    disabled: boolean;
  }) => {
    const keywordT = getFixedT(
      null,
      'policy',
      'policy.dataIdentifiers.keywordLists'
    );

    const columnDefs = [
      {
        id: 'value',
        header: keywordT('label.keyword'),
        width: '50%',
      },
      {
        id: 'caseSensitive',
        header: keywordT('label.caseSensitive'),
        cell: (index: number) => {
          return data[index].caseSensitive
            ? keywordT('label.yes')
            : keywordT('label.no');
        },
        width: '20%',
      },
      {
        id: 'score',
        header: keywordT('label.score'),
        width: '20%',
      },
      {
        id: 'action',
        header: '',
        cell: (index: number) => {
          return (
            <IconButton
              onClick={() => {
                removeByIndex(index);
              }}
              disabled={disabled}
            >
              <Icon as={DeleteIcon} />
            </IconButton>
          );
        },
        width: '10%',
      },
    ];

    return (
      <>
        <Text fontWeight="600" fontSize="18px" lineHeight="24px">
          {keywordT('list')}
        </Text>
        {data.length === 0 ? (
          <Text>{keywordT('emptyListTip')}</Text>
        ) : (
          <Table size="md" layout="flexbox" width="100%">
            <TableHeader>
              <TableHeaderRow width="100%">
                {columnDefs.map((col) => (
                  <TableHeaderCell key={col.id} width={col.width}>
                    {col.header}
                  </TableHeaderCell>
                ))}
              </TableHeaderRow>
            </TableHeader>
            <TableBody width="100%">
              {data.map((item, index) => {
                return (
                  <TableRow key={index} width="100%">
                    {columnDefs.map((col) => {
                      return (
                        <TableCell
                          key={col.id}
                          width={col.width}
                          wordBreak="break-all"
                        >
                          {col.cell
                            ? col.cell(index)
                            : item[col.id as keyof typeof item]}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </>
    );
  },
  (pre, next) => {
    return pre.data.length === next.data.length;
  }
);
